import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Shuffle } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { parseMt5CsvInWorker } from "@/lib/lytick/csv-import-worker";
import type { ParsedDataset, ColumnMap } from "@/lib/lytick/csv-import";
import { commitCsvImport, randomMid60 } from "@/hooks/useCsvImport";
import { getCurrentCsvHash } from "@/lib/lytick/drawing-persistence";
import { useChartStore } from "@/stores/chartStore";
import { useSettingsStore } from "@/stores/settingsStore";

// ── Formatting helpers ─────────────────────────────────────────────────────

function formatGranularity(seconds: number): string {
  if (seconds < 60) return `${seconds}s · ${seconds}-second bars`;
  if (seconds === 60) return `M1 · 1-minute bars`;
  if (seconds < 3600) return `M${seconds / 60} · ${seconds / 60}-minute bars`;
  if (seconds === 3600) return `H1 · 1-hour bars`;
  if (seconds < 86400) return `H${seconds / 3600} · ${seconds / 3600}-hour bars`;
  if (seconds === 86400) return `D1 · Daily bars`;
  if (seconds === 604800) return `W1 · Weekly bars`;
  return `${Math.round(seconds / 86400)}D bars`;
}

function formatDate(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 10);
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function epochToDatetimeLocal(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(0, 16);
}

function datetimeLocalToEpoch(s: string): number {
  return new Date(s + ":00Z").getTime();
}

// ── Column mapping field config ────────────────────────────────────────────

const COL_FIELDS: { key: keyof ColumnMap; label: string; required: boolean }[] = [
  { key: "dateIdx", label: "Date", required: true },
  { key: "timeIdx", label: "Time", required: false },
  { key: "openIdx", label: "Open", required: true },
  { key: "highIdx", label: "High", required: true },
  { key: "lowIdx", label: "Low", required: true },
  { key: "closeIdx", label: "Close", required: true },
  { key: "volumeIdx", label: "Volume", required: false },
];

// ── Dialog ────────────────────────────────────────────────────────────────

interface Props {
  file: File;
  dataset: ParsedDataset;
  onClose: () => void;
}

export function CsvImportPreviewDialog({ file, dataset, onClose }: Props) {
  const [symbolValue, setSymbolValue] = useState(dataset.symbol);
  const [localDataset, setLocalDataset] = useState(dataset);
  const [advancedOpen, setAdvancedOpen] = useState(dataset.usedFallback);
  const [pendingMap, setPendingMap] = useState<ColumnMap>(dataset.columnMap);
  const [isReparsing, setIsReparsing] = useState(false);
  const [reparseError, setReparseError] = useState<string | null>(null);
  const [replayStartTs, setReplayStartTs] = useState<number>(() =>
    randomMid60(dataset.firstTs, dataset.lastTs, dataset.granularitySeconds)
  );

  const setAutoLoadCsv = useSettingsStore((s) => s.setAutoLoadCsv);
  const currentSymbol = useChartStore((s) => s.symbol);
  const hasData = useChartStore((s) => s.hasData);

  const currentHash = getCurrentCsvHash();
  const isReplacing =
    hasData &&
    currentHash !== null &&
    currentHash !== localDataset.contentHash;

  function handleConfirm() {
    commitCsvImport(localDataset, symbolValue.trim() || localDataset.symbol, { replayStartTs });
  }

  function handleDontAskAgain() {
    setAutoLoadCsv(true);
    commitCsvImport(localDataset, symbolValue.trim() || localDataset.symbol, { replayStartTs });
  }

  async function handleApplyMapping() {
    setIsReparsing(true);
    setReparseError(null);
    try {
      const reparsed = await parseMt5CsvInWorker(file, undefined, { columnMap: pendingMap });
      setLocalDataset(reparsed);
      setSymbolValue((prev) => prev); // preserve user-edited symbol
    } catch (err) {
      setReparseError(err instanceof Error ? err.message : "Re-parse failed.");
    } finally {
      setIsReparsing(false);
    }
  }

  function handleMapChange(field: keyof ColumnMap, idx: number) {
    setPendingMap((prev) => ({ ...prev, [field]: idx === -1 ? null : idx }));
  }

  return (
    <Dialog.Root open onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[400px] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 rounded-xl border shadow-2xl flex flex-col overflow-hidden"
          style={{
            backgroundColor: "var(--bg-card)",
            borderColor: "var(--border-default)",
          }}
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-4 shrink-0">
            <Dialog.Title
              className="text-sm font-semibold mb-0.5"
              style={{ color: "var(--text-primary)" }}
            >
              Import CSV
            </Dialog.Title>
            <Dialog.Description
              className="text-xs"
              style={{ color: "var(--text-secondary)" }}
            >
              {file.name} · {formatSize(file.size)}
            </Dialog.Description>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 px-5 pb-2 space-y-3">

            {/* Replace notice */}
            {isReplacing && (
              <div
                className="rounded-lg border px-3 py-2 text-xs leading-relaxed"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--lyt-warning) 8%, var(--bg-elevated))",
                  borderColor: "color-mix(in srgb, var(--lyt-warning) 25%, var(--border-default))",
                  color: "var(--text-secondary)",
                }}
              >
                Replacing <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{currentSymbol}</span>.
                {" "}Drawings will be restored when you re-open this file.
              </div>
            )}

            {/* Stats grid */}
            <div
              className="rounded-lg border divide-y"
              style={{
                borderColor: "var(--border-default)",
                "--tw-divide-color": "var(--border-default)",
              } as React.CSSProperties}
            >
              {/* Symbol — editable */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 min-h-10">
                <span className="text-xs shrink-0" style={{ color: "var(--text-secondary)" }}>
                  Symbol
                </span>
                <input
                  value={symbolValue}
                  onChange={(e) => setSymbolValue(e.target.value.toUpperCase())}
                  className="text-xs text-right font-medium bg-transparent outline-none border-b w-32"
                  style={{
                    color: "var(--text-primary)",
                    borderColor: "var(--border-default)",
                  }}
                  maxLength={20}
                  spellCheck={false}
                />
              </div>

              {/* Timeframe */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 min-h-10">
                <span className="text-xs shrink-0" style={{ color: "var(--text-secondary)" }}>
                  Timeframe
                </span>
                <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
                  {formatGranularity(localDataset.granularitySeconds)}
                </span>
              </div>

              {/* Date range */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 min-h-10">
                <span className="text-xs shrink-0" style={{ color: "var(--text-secondary)" }}>
                  Range
                </span>
                <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
                  {formatDate(localDataset.firstTs)} → {formatDate(localDataset.lastTs)}
                </span>
              </div>

              {/* Bar count */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 min-h-10">
                <span className="text-xs shrink-0" style={{ color: "var(--text-secondary)" }}>
                  Bars
                </span>
                <span className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>
                  {localDataset.rowCount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Start replay from */}
            <div
              className="rounded-lg border px-3 py-2.5"
              style={{
                backgroundColor: "color-mix(in srgb, var(--accent-primary) 8%, var(--bg-elevated))",
                borderColor: "color-mix(in srgb, var(--accent-primary) 30%, var(--border-default))",
              }}
            >
              <p className="text-xs font-semibold mb-2" style={{ color: "var(--accent-primary)" }}>
                Start replay from
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="datetime-local"
                  value={epochToDatetimeLocal(replayStartTs)}
                  min={epochToDatetimeLocal(localDataset.firstTs)}
                  max={epochToDatetimeLocal(localDataset.lastTs)}
                  onChange={(e) => {
                    const ts = datetimeLocalToEpoch(e.target.value);
                    if (Number.isFinite(ts)) setReplayStartTs(ts);
                  }}
                  className="flex-1 rounded border px-2 py-1 text-xs outline-none"
                  style={{
                    backgroundColor: "var(--bg-surface)",
                    borderColor: "color-mix(in srgb, var(--accent-primary) 30%, var(--border-default))",
                    color: "var(--text-primary)",
                    colorScheme: "dark",
                  }}
                />
                <button
                  type="button"
                  title="Pick a random date"
                  onClick={() => setReplayStartTs(randomMid60(localDataset.firstTs, localDataset.lastTs, localDataset.granularitySeconds))}
                  className="flex items-center justify-center rounded border p-1.5 transition-colors hover:bg-[var(--surface-tint-hover)]"
                  style={{
                    borderColor: "color-mix(in srgb, var(--accent-primary) 30%, var(--border-default))",
                    color: "var(--text-secondary)",
                  }}
                >
                  <Shuffle className="size-3.5" />
                </button>
              </div>
              <p className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
                Replay starts here — future bars hidden until revealed.
              </p>
            </div>

            {/* Fallback warning */}
            {localDataset.usedFallback && (
              <div
                className="flex items-start gap-2 rounded-lg border px-3 py-2 text-xs"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--lyt-warning) 8%, var(--bg-elevated))",
                  borderColor: "color-mix(in srgb, var(--lyt-warning) 25%, var(--border-default))",
                  color: "var(--text-secondary)",
                }}
              >
                <AlertTriangle
                  className="mt-0.5 shrink-0 size-3.5"
                  style={{ color: "var(--lyt-warning)" }}
                />
                <span>
                  Column headers were not recognised — using positional layout.
                  Review the mapping in Advanced if the data looks wrong.
                </span>
              </div>
            )}

            {/* Parse warnings */}
            {localDataset.warnings.length > 0 && (
              <div
                className="rounded-lg border px-3 py-2 text-xs space-y-1"
                style={{
                  backgroundColor: "color-mix(in srgb, var(--lyt-warning) 8%, var(--bg-elevated))",
                  borderColor: "color-mix(in srgb, var(--lyt-warning) 25%, var(--border-default))",
                }}
              >
                <p style={{ color: "var(--lyt-warning)", fontWeight: 500 }}>
                  {localDataset.warnings.length} row{localDataset.warnings.length !== 1 ? "s" : ""} skipped
                </p>
                {localDataset.warnings.map((w, i) => (
                  <p key={i} className="font-mono truncate" style={{ color: "var(--text-secondary)" }}>
                    {w}
                  </p>
                ))}
              </div>
            )}

            {/* Advanced — column mapping */}
            <div
              className="rounded-lg border overflow-hidden"
              style={{ borderColor: "var(--border-default)" }}
            >
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="flex w-full items-center justify-between px-3 py-2 text-xs transition-colors hover:bg-[var(--surface-tint-hover)]"
                style={{
                  backgroundColor: "var(--bg-elevated)",
                  color: "var(--text-secondary)",
                }}
              >
                <span>Advanced · Column mapping</span>
                {advancedOpen
                  ? <ChevronDown className="size-3.5 shrink-0" />
                  : <ChevronRight className="size-3.5 shrink-0" />
                }
              </button>

              {advancedOpen && (
                <div
                  className="px-3 py-3 space-y-2"
                  style={{ borderTop: "1px solid var(--border-default)" }}
                >
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                    Assign which column contains each field. Change and press Apply to re-parse.
                  </p>

                  {COL_FIELDS.map(({ key, label, required }) => {
                    const currentIdx = pendingMap[key] as number | null;
                    return (
                      <div key={key} className="flex items-center justify-between gap-3">
                        <span className="text-xs shrink-0 w-14" style={{ color: "var(--text-secondary)" }}>
                          {label}
                        </span>
                        <select
                          value={currentIdx === null ? -1 : currentIdx}
                          onChange={(e) => handleMapChange(key, Number(e.target.value))}
                          className="flex-1 rounded border px-2 py-1 text-xs outline-none"
                          style={{
                            backgroundColor: "var(--bg-surface)",
                            borderColor: "var(--border-default)",
                            color: "var(--text-primary)",
                          }}
                        >
                          {!required && (
                            <option value={-1}>— None —</option>
                          )}
                          {localDataset.headers.map((h, i) => (
                            <option key={i} value={i}>{h || `Col ${i}`}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}

                  {reparseError && (
                    <p className="text-xs" style={{ color: "var(--color-loss)" }}>{reparseError}</p>
                  )}

                  <button
                    type="button"
                    onClick={() => void handleApplyMapping()}
                    disabled={isReparsing}
                    className="mt-1 w-full rounded-lg py-1.5 text-xs font-medium transition-all active:scale-95 disabled:opacity-50"
                    style={{
                      backgroundColor: "var(--bg-overlay)",
                      color: "var(--text-primary)",
                    }}
                  >
                    {isReparsing ? "Applying…" : "Apply mapping"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between gap-2 border-t px-5 py-3 shrink-0"
            style={{
              backgroundColor: "var(--bg-elevated)",
              borderColor: "var(--border-default)",
            }}
          >
            <button
              type="button"
              onClick={handleDontAskAgain}
              className="text-xs transition-colors hover:underline"
              style={{ color: "var(--text-muted)" }}
            >
              Don't show again
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border px-4 py-1.5 text-xs transition-colors hover:bg-[var(--surface-tint-hover)]"
                style={{
                  borderColor: "var(--border-default)",
                  color: "var(--text-secondary)",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="rounded-lg px-4 py-1.5 text-xs font-medium transition-all active:scale-95"
                style={{
                  backgroundColor: "var(--accent-primary)",
                  color: "var(--text-primary)",
                }}
              >
                Start Replay
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
